import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  MappingRequest,
  getCardMapping,
  getCashReceiptMapping,
  getTaxInvoiceMapping,
  deleteMappings,
} from '~apis/bank-transaction-detail';

import { TransactionsQueryKey } from '../query';

export const MappingQueryKey = {
  taxInvoice: (request: { companyId: string; transactionId: number }) => [
    'tax-invoice-mapping',
    request.companyId,
    request.transactionId,
  ],
  card: (request: { companyId: string; transactionId: number }) => [
    'card-mapping,Z',
    request.companyId,
    request.transactionId,
  ],
  cashReceipt: (request: { companyId: string; transactionId: number }) => [
    'cash-receipt-mapping',
    request.companyId,
    request.transactionId,
  ],
  taxInvoiceDetail: (request: MappingRequest) => [
    'tax-invoice-mapping',
    request.companyId,
    request.transactionId,
    request.labelId,
  ],
  cardDetail: (request: MappingRequest) => [
    'card-mapping,Z',
    request.companyId,
    request.transactionId,
    request.labelId,
  ],
  cashReceiptDetail: (request: MappingRequest) => [
    'cash-receipt-mapping',
    request.companyId,
    request.transactionId,
    request.labelId,
  ],
};

export function useTaxInvoiceMapping(request: MappingRequest) {
  return useQuery(MappingQueryKey.taxInvoiceDetail(request), () => getTaxInvoiceMapping(request));
}

export function useCardMapping(request: MappingRequest) {
  return useQuery(MappingQueryKey.cardDetail(request), () => getCardMapping(request));
}

export function useCashReceiptMapping(request: MappingRequest) {
  return useQuery(MappingQueryKey.cashReceiptDetail(request), () => getCashReceiptMapping(request));
}

export function useDeleteMappings(preventRefetch: boolean = false) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyId: string; transactionId: number; labelId: number }) =>
      deleteMappings(data),
    onSuccess: (_, variables) => {
      const { companyId, transactionId } = variables;
      if (!preventRefetch) {
        queryClient.invalidateQueries(TransactionsQueryKey.default());
      }

      queryClient.invalidateQueries(MappingQueryKey.taxInvoice({ companyId, transactionId }));
      queryClient.invalidateQueries(MappingQueryKey.card({ companyId, transactionId }));
      queryClient.invalidateQueries(MappingQueryKey.cashReceipt({ companyId, transactionId }));

      queryClient.invalidateQueries(TransactionsQueryKey.detail({ companyId, transactionId }));
    },
  });
}
